/**
 * 보고서 보관함 — 승인된 완성보고서 원본: bt-reports/승인/{연도}/{등록번호}_{교회명}/{일련번호}_{이름}.jpg
 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { Search as SearchIcon, FolderOutlined } from '@mui/icons-material';
import { api, fileUrl } from '../api';
import { C } from '../common';
import { Empty } from './ui';

type Archive = { years: string[]; year: string; basePath: string; churches: Array<{ folder: string; regNo: string; churchName: string; files: Array<{ name: string; size: number; path: string }> }> };

const ArchiveTab: React.FC = () => {
  const [year, setYear] = useState('');
  const [data, setData] = useState<Archive | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string>('');
  const [error, setError] = useState('');
  useEffect(() => {
    api<{ data: Archive }>(`/admin/cert/archive${year ? `?year=${year}` : ''}`).then((r) => { setData(r.data); if (!year) setYear(r.data.year); }).catch((e) => setError(e.message));
  }, [year]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;
  const kw = q.trim();
  const churches = data.churches.filter((c) => !kw || c.folder.includes(kw) || c.files.some((f) => f.name.includes(kw)));
  const total = data.churches.reduce((n, c) => n + c.files.length, 0);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <TextField select size="small" label="연도" value={year} onChange={(e) => setYear(e.target.value)} sx={{ width: 110 }}>
          {(data.years.length ? data.years : [data.year]).map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField size="small" placeholder="교회 · 등록번호 · 일련번호 · 이름" value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="contained" disabled={!total} href={fileUrl(`/admin/cert/archive/zip?year=${year}`)}>⬇ {year}년 전체 ZIP</Button>
      </Box>
      <Typography fontSize={13} color={C.sub} mb={1.5}>서버 저장 위치: <b>{data.basePath}/{year}/</b> · 교회 {data.churches.length}곳 · 파일 {total}개 · 승인을 취소하면 여기서도 지워집니다.</Typography>
      {!churches.length && <Empty>보관된 보고서가 없습니다.</Empty>}
      <Box sx={{ display: 'grid', gap: 1 }}>
        {churches.map((c) => (
          <Box key={c.folder} sx={{ border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer' }} onClick={() => setOpen(open === c.folder ? '' : c.folder)}>
              <FolderOutlined sx={{ color: '#d99a00' }} />
              <Typography fontWeight={800} sx={{ flex: 1 }}>{c.folder} <span style={{ color: C.sub, fontWeight: 500, fontSize: 13 }}>· {c.files.length}개</span></Typography>
              <Button size="small" variant="outlined" href={fileUrl(`/admin/cert/archive/zip?year=${year}&folder=${encodeURIComponent(c.folder)}`)} onClick={(e) => e.stopPropagation()}>ZIP</Button>
            </Box>
            {(open === c.folder || kw) && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1, p: 1.5, borderTop: `1px solid ${C.line}` }}>
                {c.files.filter((f) => !kw || c.folder.includes(kw) || f.name.includes(kw)).map((f) => {
                  const url = fileUrl(`/admin/cert/archive/file?path=${encodeURIComponent(f.path)}`);
                  return (
                    <Box key={f.name} component="a" href={url} target="_blank" sx={{ textDecoration: 'none', color: 'inherit', border: `1px solid ${C.line}`, borderRadius: 1.5, overflow: 'hidden' }}>
                      {/\.pdf$/i.test(f.name)
                        ? <Box sx={{ height: 110, display: 'grid', placeItems: 'center', bgcolor: '#f7f3e3', fontWeight: 800, color: C.sub }}>PDF</Box>
                        : <img src={url} alt={f.name} loading="lazy" style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />}
                      <Typography fontSize={11.5} sx={{ p: 0.75, wordBreak: 'break-all' }}>{f.name}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ArchiveTab;
